/**
 * Dungeon tile generator — Style Bible §6.
 *
 * The point of this file is combinatorics, not illustration. 4 floors × 4 wall
 * sets × 6 prop sets × 3 light sets = 288 distinct rooms from a handful of
 * generators. That is what makes GOD-3's "30 rooms" and GOD-4's seven regions
 * affordable: a new room is a combination, not a new painting.
 *
 * Print direction (Style Bible v2.0): the isometric framing stays — box lids and
 * room boards of the era were drawn that way — but nothing here is *rendered*.
 * Every surface is a flat spot ink or a halftone screen of one, every edge is a
 * black key line, and "light" is a difference in screen density, never a wash.
 */

import {
  INK,
  regionInk,
  LINE,
  PAPER,
  SPOT,
  HALFTONE,
  HALFTONE_ANGLE,
  MISREGISTRATION,
  TILE_SPEC,
  type RegionId,
} from './tokens.js';
import {
  ArtRng,
  circle,
  defs,
  ellipse,
  group,
  halftonePattern,
  misregistered,
  n,
  paperGrain,
  path,
  polygon,
  svg,
  tag,
} from './svg.js';

export type FloorSet = 'flagstone' | 'packedEarth' | 'tiledMosaic' | 'floodedStone';
export type WallSet = 'cutBlock' | 'rubble' | 'carvedPillar' | 'boneNiche';
export type PropSet = 'none' | 'altar' | 'chest' | 'bones' | 'mirrorShards' | 'throne';
export type LightSet = 'candle' | 'shaft' | 'gloom';

export type RoomKind =
  | 'combat'
  | 'npc'
  | 'treasure'
  | 'trap'
  | 'shop'
  | 'puzzle'
  | 'event'
  | 'secret'
  | 'boss'
  | 'entrance';

/**
 * Room kind is expressed through props and lighting only (Style Bible §6), so a
 * new room kind never needs new base art.
 */
export const KIND_RECIPE: Readonly<Record<RoomKind, { prop: PropSet; light: LightSet }>> = {
  entrance: { prop: 'none', light: 'shaft' },
  combat: { prop: 'bones', light: 'candle' },
  npc: { prop: 'altar', light: 'candle' },
  treasure: { prop: 'chest', light: 'candle' },
  trap: { prop: 'none', light: 'gloom' },
  shop: { prop: 'chest', light: 'candle' },
  puzzle: { prop: 'mirrorShards', light: 'gloom' },
  event: { prop: 'altar', light: 'gloom' },
  secret: { prop: 'none', light: 'shaft' },
  boss: { prop: 'throne', light: 'shaft' },
};

export interface TileRenderOptions {
  readonly floor?: FloorSet;
  readonly wall?: WallSet;
  readonly prop?: PropSet;
  readonly light?: LightSet;
  readonly region?: RegionId;
  readonly size?: number;
  readonly seed?: string;
}

/** Pattern ids, kept stable so the output stays byte-identical for a given seed. */
const P = {
  grain: 'tg',
  floorScreen: 'tfs',
  wallScreen: 'tws',
  deepScreen: 'tds',
  gloomScreen: 'tgl',
  warmScreen: 'twm',
} as const;

/** Isometric diamond corners for the floor plate. */
function isoDiamond(size: number): [number, number][] {
  const cx = size / 2;
  const w = size * 0.46;
  const h = w * TILE_SPEC.isoRatio;
  const cy = size * 0.58;
  return [
    [cx, cy - h],
    [cx + w, cy],
    [cx, cy + h],
    [cx - w, cy],
  ];
}

/**
 * A printed drop shadow is a patch of screen, not a translucent black blob.
 * Everything that sits on the floor gets one of these so props do not float.
 */
function contactPatch(cx: number, cy: number, rx: number, ry: number): string {
  return ellipse({ cx, cy, rx, ry, fill: `url(#${P.deepScreen})` });
}

function floorLayer(floor: FloorSet, size: number, rng: ArtRng): string {
  const pts = isoDiamond(size);
  const s = size / 512;
  const cx = size / 2;
  const cy = size * 0.58;
  const w = size * 0.46;
  const h = w * TILE_SPEC.isoRatio;

  // Colour plate first, keyline on top — the plate is what sits a hair off.
  const plate = misregistered(
    `floor:${floor}`,
    size,
    MISREGISTRATION,
    polygon(pts, { fill: PAPER.board }) + polygon(pts, { fill: `url(#${P.floorScreen})` }),
  );
  const key = polygon(pts, {
    fill: 'none',
    stroke: INK.key,
    'stroke-width': LINE.outline * s,
    'stroke-linejoin': 'round',
  });

  let detail = '';
  if (floor === 'flagstone' || floor === 'tiledMosaic') {
    // Iso grid lines, so the floor reads as laid stone rather than a flat plane.
    const divisions = floor === 'tiledMosaic' ? 6 : 4;
    for (let i = 1; i < divisions; i++) {
      const t = i / divisions;
      detail += path(`M${n(cx - w + w * t)} ${n(cy - h * t)} L${n(cx + w * t)} ${n(cy + h - h * t)}`, {
        stroke: INK.key,
        'stroke-width': LINE.detail * s,
      });
      detail += path(`M${n(cx + w - w * t)} ${n(cy - h * t)} L${n(cx - w * t)} ${n(cy + h - h * t)}`, {
        stroke: INK.key,
        'stroke-width': LINE.detail * s,
      });
    }
  }
  if (floor === 'packedEarth') {
    // Stipple: the printer's way of saying "loose dirt". Solid key dots, no wash.
    for (let i = 0; i < 26; i++) {
      detail += circle({
        cx: cx + rng.range(-w * 0.8, w * 0.8),
        cy: cy + rng.range(-h * 0.7, h * 0.7),
        r: rng.range(0.8, 2.2) * s,
        fill: INK.key,
      });
    }
  }
  if (floor === 'floodedStone') {
    // Standing water: a blue plate under ruled reflection lines, the way a flat
    // press prints water. No transparency anywhere.
    detail += misregistered(
      'flood',
      size,
      MISREGISTRATION,
      polygon(pts, { fill: `url(#${P.wallScreen})` }),
    );
    for (let i = 0; i < 3; i++) {
      const y = cy - h * 0.3 + i * h * 0.35;
      detail += path(`M${n(cx - w * 0.5)} ${n(y)} h${n(w)}`, {
        stroke: SPOT.blue,
        'stroke-width': LINE.structure * s,
        'stroke-linecap': 'round',
      });
    }
  }
  return plate + detail + key;
}

function wallLayer(wall: WallSet, size: number, rng: ArtRng): string {
  const cx = size / 2;
  const cy = size * 0.58;
  const w = size * 0.46;
  const h = w * TILE_SPEC.isoRatio;
  const wallH = size * 0.26;
  const s = size / 512;
  const sw = LINE.outline * s;

  const leftPts: [number, number][] = [
    [cx - w, cy],
    [cx, cy - h],
    [cx, cy - h - wallH],
    [cx - w, cy - wallH],
  ];
  const rightPts: [number, number][] = [
    [cx + w, cy],
    [cx, cy - h],
    [cx, cy - h - wallH],
    [cx + w, cy - wallH],
  ];

  /*
   * The two walls differ by SCREEN DENSITY, not by shade. That is how a press
   * with one black plate shows a lit face and a shaded one, and it is the single
   * thing that stops this reading as a 3D render.
   */
  const plates = misregistered(
    `wall:${wall}`,
    size,
    MISREGISTRATION,
    polygon(leftPts, { fill: PAPER.aged }) +
      polygon(rightPts, { fill: PAPER.aged }) +
      polygon(rightPts, { fill: `url(#${P.wallScreen})` }),
  );
  const keys =
    polygon(leftPts, { fill: 'none', stroke: INK.key, 'stroke-width': sw, 'stroke-linejoin': 'round' }) +
    polygon(rightPts, { fill: 'none', stroke: INK.key, 'stroke-width': sw, 'stroke-linejoin': 'round' });

  let detail = '';
  switch (wall) {
    case 'cutBlock': {
      for (let row = 0; row < 3; row++) {
        const y = cy - wallH + (wallH / 3) * row;
        detail += path(`M${n(cx - w)} ${n(y)} L${n(cx)} ${n(y - h)}`, { stroke: INK.key, 'stroke-width': LINE.detail * s });
        detail += path(`M${n(cx + w)} ${n(y)} L${n(cx)} ${n(y - h)}`, { stroke: INK.key, 'stroke-width': LINE.detail * s });
      }
      break;
    }
    case 'rubble': {
      for (let i = 0; i < 14; i++) {
        const side = rng.next() < 0.5 ? -1 : 1;
        const t = rng.range(0.15, 0.9);
        detail += polygon(
          [
            [cx + side * w * t, cy - h * t * 0.9 - rng.range(0, wallH * 0.6)],
            [cx + side * w * t + rng.range(6, 16) * s, cy - h * t * 0.9 - rng.range(0, wallH * 0.6) + rng.range(4, 12) * s],
            [cx + side * w * t - rng.range(4, 12) * s, cy - h * t * 0.9 - rng.range(0, wallH * 0.5) + rng.range(6, 14) * s],
          ],
          { fill: `url(#${P.deepScreen})`, stroke: INK.key, 'stroke-width': LINE.detail * s, 'stroke-linejoin': 'round' },
        );
      }
      break;
    }
    case 'carvedPillar': {
      for (const side of [-1, 1]) {
        const px = cx + side * w * 0.55;
        const py = cy - h * 0.55;
        detail += tag('rect', {
          x: px - 9 * s,
          y: py - wallH,
          width: 18 * s,
          height: wallH,
          fill: PAPER.card,
          stroke: INK.key,
          'stroke-width': LINE.structure * s,
        });
        detail += tag('rect', {
          x: px - 13 * s,
          y: py - wallH - 7 * s,
          width: 26 * s,
          height: 8 * s,
          fill: PAPER.card,
          stroke: INK.key,
          'stroke-width': LINE.structure * s,
        });
      }
      break;
    }
    case 'boneNiche': {
      for (let i = 0; i < 4; i++) {
        const side = i % 2 === 0 ? -1 : 1;
        const t = 0.3 + Math.floor(i / 2) * 0.35;
        const nx = cx + side * w * t;
        const ny = cy - h * t - wallH * 0.55;
        // The niche: a solid key arch, then the skull printed on paper inside it.
        detail += path(
          `M${n(nx - 12 * s)} ${n(ny + 16 * s)} v${n(-20 * s)} a${n(12 * s)} ${n(12 * s)} 0 0 1 ${n(24 * s)} 0 v${n(20 * s)} Z`,
          { fill: INK.key },
        );
        detail += circle({ cx: nx, cy: ny + 2 * s, r: 5 * s, fill: PAPER.card });
      }
      break;
    }
  }
  return plates + detail + keys;
}

function propLayer(prop: PropSet, size: number, rng: ArtRng): string {
  const cx = size / 2;
  const cy = size * 0.58;
  const s = size / 512;
  const sw = LINE.structure * s;

  switch (prop) {
    case 'none':
      return '';
    case 'altar':
      return (
        contactPatch(cx, cy + 26 * s, 46 * s, 14 * s) +
        polygon(
          [
            [cx - 40 * s, cy + 6 * s],
            [cx, cy - 14 * s],
            [cx + 40 * s, cy + 6 * s],
            [cx, cy + 26 * s],
          ],
          { fill: PAPER.card, stroke: INK.key, 'stroke-width': sw, 'stroke-linejoin': 'round' },
        ) +
        tag('rect', { x: cx - 28 * s, y: cy - 44 * s, width: 56 * s, height: 34 * s, fill: `url(#${P.wallScreen})`, stroke: INK.key, 'stroke-width': sw }) +
        circle({ cx, cy: cy - 54 * s, r: 9 * s, fill: SPOT.yellow, stroke: INK.key, 'stroke-width': LINE.detail * s })
      );
    case 'chest':
      return (
        contactPatch(cx, cy + 24 * s, 40 * s, 12 * s) +
        tag('rect', { x: cx - 34 * s, y: cy - 18 * s, width: 68 * s, height: 40 * s, rx: 3 * s, fill: SPOT.orange, stroke: INK.key, 'stroke-width': sw }) +
        path(`M${n(cx - 34 * s)} ${n(cy - 18 * s)} a${n(34 * s)} ${n(22 * s)} 0 0 1 ${n(68 * s)} 0 Z`, { fill: PAPER.aged, stroke: INK.key, 'stroke-width': sw }) +
        tag('rect', { x: cx - 6 * s, y: cy - 8 * s, width: 12 * s, height: 16 * s, fill: SPOT.yellow, stroke: INK.key, 'stroke-width': LINE.detail * s })
      );
    case 'bones': {
      let out = '';
      for (let i = 0; i < 7; i++) {
        const x = cx + rng.range(-60, 60) * s;
        const y = cy + rng.range(-14, 22) * s;
        const rot = rng.range(-70, 70);
        out += group(
          { transform: `rotate(${n(rot)} ${n(x)} ${n(y)})` },
          tag('rect', { x: x - 16 * s, y: y - 3.2 * s, width: 32 * s, height: 6.4 * s, rx: 3.2 * s, fill: PAPER.card, stroke: INK.key, 'stroke-width': LINE.detail * s }) +
            circle({ cx: x - 17 * s, cy: y, r: 4.5 * s, fill: PAPER.card, stroke: INK.key, 'stroke-width': LINE.detail * s }) +
            circle({ cx: x + 17 * s, cy: y, r: 4.5 * s, fill: PAPER.card, stroke: INK.key, 'stroke-width': LINE.detail * s }),
        );
      }
      return out;
    }
    case 'mirrorShards': {
      let out = '';
      for (let i = 0; i < 8; i++) {
        const x = cx + rng.range(-70, 70) * s;
        const y = cy + rng.range(-20, 24) * s;
        // Alternate the plate so the shards read as catching different light —
        // two flat inks, which is exactly how a four-colour job faked a glint.
        const fill = i % 2 === 0 ? PAPER.card : SPOT.blue;
        out += polygon(
          [
            [x, y - rng.range(12, 30) * s],
            [x + rng.range(6, 14) * s, y + rng.range(4, 12) * s],
            [x - rng.range(6, 14) * s, y + rng.range(4, 12) * s],
          ],
          { fill, stroke: INK.key, 'stroke-width': LINE.detail * s, 'stroke-linejoin': 'round' },
        );
      }
      return out;
    }
    case 'throne':
      return (
        contactPatch(cx, cy + 34 * s, 58 * s, 16 * s) +
        tag('rect', { x: cx - 34 * s, y: cy - 12 * s, width: 68 * s, height: 44 * s, fill: `url(#${P.wallScreen})`, stroke: INK.key, 'stroke-width': sw }) +
        tag('rect', { x: cx - 40 * s, y: cy - 96 * s, width: 80 * s, height: 88 * s, rx: 6 * s, fill: `url(#${P.deepScreen})`, stroke: INK.key, 'stroke-width': sw }) +
        // The crown itself, unworn — the image the boss room is named for.
        path(
          `M${n(cx - 22 * s)} ${n(cy - 30 * s)} l${n(8 * s)} ${n(-18 * s)} l${n(7 * s)} ${n(11 * s)} l${n(7 * s)} ${n(-14 * s)} l${n(7 * s)} ${n(14 * s)} l${n(7 * s)} ${n(-11 * s)} l${n(8 * s)} ${n(18 * s)} Z`,
          { fill: SPOT.yellow, stroke: INK.key, 'stroke-width': LINE.structure * s, 'stroke-linejoin': 'round' },
        )
      );
  }
}

/**
 * Light, printed.
 *
 * A press cannot print "darker". It prints more dots, or it outlines the bright
 * thing and leaves the paper bare. Both of those are here; neither is a wash.
 *
 * Light is drawn UNDER the props, for the same reason a printer laid the tint
 * before the line art: a beam printed over the throne hides the throne, and the
 * throne is the reason the room exists.
 */
function lightLayer(light: LightSet, size: number): string {
  const cx = size / 2;
  const cy = size * 0.58;
  const s = size / 512;
  const w = size * 0.46;
  const h = w * TILE_SPEC.isoRatio;

  switch (light) {
    case 'candle':
      // A screened pool on the floor in the region's ink. It sits inside the
      // diamond, so it reads as light falling on the floor, not as a sticker.
      return polygon(
        [
          [cx, cy - h * 0.55],
          [cx + w * 0.55, cy],
          [cx, cy + h * 0.55],
          [cx - w * 0.55, cy],
        ],
        { fill: `url(#${P.warmScreen})` },
      );
    case 'shaft': {
      // Bare paper inside a key outline, hatched — the classic printed sunbeam.
      // Narrow, and it stops at the floor where its pool is.
      // Offset left of centre on purpose: the middle of the floor is where the
      // prop stands, and a beam printed across the throne hides the throne.
      const topL = cx - size * 0.3;
      const topR = cx - size * 0.21;
      const botL = cx - size * 0.24;
      const botR = cx - size * 0.12;
      const top = size * 0.16;
      const pts: [number, number][] = [
        [topL, top],
        [topR, top],
        [botR, cy],
        [botL, cy],
      ];
      let hatch = '';
      for (let i = 1; i < 3; i++) {
        const t = i / 3;
        hatch += path(
          `M${n(topL + (topR - topL) * t)} ${n(top)} L${n(botL + (botR - botL) * t)} ${n(cy)}`,
          { stroke: INK.faded, 'stroke-width': LINE.detail * s },
        );
      }
      const pool = ellipse({ cx: (botL + botR) / 2, cy, rx: w * 0.3, ry: h * 0.3, fill: PAPER.card, stroke: INK.faded, 'stroke-width': LINE.detail * s });
      return (
        pool +
        polygon(pts, { fill: PAPER.card }) +
        hatch +
        polygon(pts, { fill: 'none', stroke: INK.key, 'stroke-width': LINE.structure * s, 'stroke-linejoin': 'round' })
      );
    }
    case 'gloom':
      // The room is unlit: one more screen, but only on the floor. Screening the
      // margins too just makes the panel look dirty.
      return polygon(
        [
          [cx, cy - h],
          [cx + w, cy],
          [cx, cy + h],
          [cx - w, cy],
        ],
        { fill: `url(#${P.deepScreen})` },
      );
  }
}

export function renderTile(opts: TileRenderOptions = {}): string {
  const size = opts.size ?? TILE_SPEC.size;
  const region = opts.region ?? 'aurelia';
  const seed = opts.seed ?? `${opts.floor}:${opts.wall}:${opts.prop}:${opts.light}:${region}`;
  const rng = new ArtRng(`tile:${seed}`);
  const ink = regionInk(region);
  const s = size / 512;

  const floor = opts.floor ?? 'flagstone';
  const wall = opts.wall ?? 'cutBlock';
  const prop = opts.prop ?? 'none';
  const light = opts.light ?? 'candle';

  /*
   * The screens are the whole palette. Regions differ because ONE of them — the
   * floor tint and the candle pool — is printed in the region's spot ink; the
   * rest of the plate is the same black screen everywhere (Style Bible §1.5).
   */
  const screens =
    paperGrain(P.grain, INK.key, `tile:${seed}`) +
    halftonePattern(P.floorScreen, ink, HALFTONE.light.dot, HALFTONE.light.grid, HALFTONE_ANGLE) +
    halftonePattern(P.warmScreen, ink, HALFTONE.mid.dot, HALFTONE.mid.grid, HALFTONE_ANGLE) +
    halftonePattern(P.wallScreen, INK.key, HALFTONE.mid.dot, HALFTONE.mid.grid, HALFTONE_ANGLE) +
    halftonePattern(P.deepScreen, INK.key, HALFTONE.dark.dot, HALFTONE.dark.grid, HALFTONE_ANGLE) +
    halftonePattern(P.gloomScreen, INK.key, HALFTONE.shadow.dot, HALFTONE.shadow.grid, HALFTONE_ANGLE);

  const body =
    defs(screens) +
    tag('rect', { width: size, height: size, fill: PAPER.card }) +
    tag('rect', { width: size, height: size, fill: `url(#${P.grain})` }) +
    wallLayer(wall, size, rng) +
    floorLayer(floor, size, rng) +
    lightLayer(light, size) +
    propLayer(prop, size, rng) +
    // Trim line: every printed panel of this era had one.
    tag('rect', {
      x: LINE.outline * s,
      y: LINE.outline * s,
      width: size - LINE.outline * s * 2,
      height: size - LINE.outline * s * 2,
      fill: 'none',
      stroke: INK.key,
      'stroke-width': LINE.structure * s,
    });

  return svg(size, size, body);
}

/** A tile for a room kind, per KIND_RECIPE. */
export function renderRoomTile(
  kind: RoomKind,
  opts: Omit<TileRenderOptions, 'prop' | 'light'> = {},
): string {
  const recipe = KIND_RECIPE[kind];
  return renderTile({ ...opts, prop: recipe.prop, light: recipe.light, seed: opts.seed ?? `kind:${kind}` });
}

export const FLOOR_SETS: readonly FloorSet[] = ['flagstone', 'packedEarth', 'tiledMosaic', 'floodedStone'];
export const WALL_SETS: readonly WallSet[] = ['cutBlock', 'rubble', 'carvedPillar', 'boneNiche'];
export const PROP_SETS: readonly PropSet[] = ['none', 'altar', 'chest', 'bones', 'mirrorShards', 'throne'];
export const LIGHT_SETS: readonly LightSet[] = ['candle', 'shaft', 'gloom'];

/** How many distinct rooms the module system can express. */
export const TILE_COMBINATIONS =
  FLOOR_SETS.length * WALL_SETS.length * PROP_SETS.length * LIGHT_SETS.length;

/** One tile per room kind, plus the base sets for reference. */
export function allTileAssets(): { id: string; svg: string }[] {
  const out: { id: string; svg: string }[] = [];
  for (const kind of Object.keys(KIND_RECIPE) as RoomKind[]) {
    out.push({ id: `tile.kind.${kind}`, svg: renderRoomTile(kind) });
  }
  for (const floor of FLOOR_SETS) {
    out.push({ id: `tile.floor.${floor}`, svg: renderTile({ floor, prop: 'none', light: 'candle' }) });
  }
  for (const wall of WALL_SETS) {
    out.push({ id: `tile.wall.${wall}`, svg: renderTile({ wall, prop: 'none', light: 'candle' }) });
  }
  return out;
}
